/**
 * 主布局组件
 * 包含侧边栏导航和内容区域
 */

import React, { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  FileText,
  ClipboardList,
  Users,
  Shield,
  Tag,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  MessageSquare,
  Activity,
  Mic,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import styles from './MainLayout.module.css';

interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  permission?: string; // 所需权限名称
}

const navItems: NavItem[] = [
  {
    to: '/knowledge-chat',
    icon: <MessageSquare size={20} strokeWidth={1.5} />,
    label: '知识问答',
    permission: '知识问答对话',
  },
  {
    to: '/work-record',
    icon: <Mic size={20} strokeWidth={1.5} />,
    label: '日常工作',
    permission: '日常工作',
  },
  {
    to: '/knowledge',
    icon: <BookOpen size={20} strokeWidth={1.5} />,
    label: '知识管理',
    permission: '知识管理',
  },
  {
    to: '/task-management',
    icon: <ClipboardList size={20} strokeWidth={1.5} />,
    label: '任务管理',
    permission: '任务管理',
  },
  {
    to: '/my-tasks',
    icon: <ClipboardList size={20} strokeWidth={1.5} />,
    label: '我的任务',
    permission: '我的任务',
  },
  {
    to: '/departments',
    icon: <FileText size={20} strokeWidth={1.5} />,
    label: '部门管理',
    permission: '部门管理',
  },
  {
    to: '/users',
    icon: <Users size={20} strokeWidth={1.5} />,
    label: '用户管理',
    permission: '用户管理',
  },
  {
    to: '/roles',
    icon: <Shield size={20} strokeWidth={1.5} />,
    label: '角色管理',
    permission: '角色管理',
  },
  // 标签管理暂时隐藏
  // {
  //   to: '/tags',
  //   icon: <Tag size={20} strokeWidth={1.5} />,
  //   label: '标签管理',
  //   permission: '标签管理',
  // },
  {
    to: '/operation-logs',
    icon: <Activity size={20} strokeWidth={1.5} />,
    label: '操作日志',
    permission: '操作日志',
  },
];

export default function MainLayout() {
  const { user, logout, hasPermission } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const filteredNavItems = navItems.filter((item) => {
    if (!item.permission) return true;
    return hasPermission(item.permission);
  });

  const handleLogout = () => {
    logout();
  };

  const closeMobileMenu = () => {
    setIsMobileOpen(false);
  };

  return (
    <div className={styles.container}>
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeMobileMenu}
          />
        )}
      </AnimatePresence>

      <aside
        className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ''} ${
          isMobileOpen ? styles.mobileOpen : ''
        }`}
      >
        <div className={styles.logoSection}>
          <div className={styles.logo}>
            <BookOpen size={24} strokeWidth={1.5} />
          </div>
          {!isCollapsed && (
            <motion.div
              className={styles.logoText}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <span className={styles.logoTitle}>大满书院</span>
              <span className={styles.logoSubtitle}>知识传承系统</span>
            </motion.div>
          )}
        </div>

        <nav className={styles.nav}>
          {filteredNavItems.map((item, index) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.active : ''}`
              }
              onClick={closeMobileMenu}
            >
              <motion.div
                className={styles.navItemContent}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <span className={styles.navIcon}>{item.icon}</span>
                {!isCollapsed && (
                  <motion.span
                    className={styles.navLabel}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 + 0.1 }}
                  >
                    {item.label}
                  </motion.span>
                )}
              </motion.div>
              {!isCollapsed && <div className={styles.navIndicator} />}
            </NavLink>
          ))}
        </nav>

        <div className={styles.userSection}>
          <div className={styles.userInfo}>
            <div className={styles.avatar}>
              {user?.user_name?.charAt(0) || 'U'}
            </div>
            {!isCollapsed && (
              <motion.div
                className={styles.userDetails}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <span className={styles.userName}>{user?.user_name}</span>
                <span className={styles.userRole}>{user?.role_name}</span>
              </motion.div>
            )}
          </div>
          <button
            className={styles.logoutButton}
            onClick={handleLogout}
            title="退出登录"
          >
            <LogOut size={18} strokeWidth={1.5} />
          </button>
        </div>

        <button
          className={styles.collapseButton}
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? '展开侧边栏' : '收起侧边栏'}
        >
          {isCollapsed ? (
            <ChevronRight size={18} strokeWidth={1.5} />
          ) : (
            <ChevronLeft size={18} strokeWidth={1.5} />
          )}
        </button>

        <button className={styles.mobileCloseButton} onClick={closeMobileMenu}>
          <X size={20} strokeWidth={1.5} />
        </button>
      </aside>

      <main className={styles.main}>
        <header className={styles.mobileHeader}>
          <button
            className={styles.menuButton}
            onClick={() => setIsMobileOpen(true)}
          >
            <Menu size={24} strokeWidth={1.5} />
          </button>
          <h1 className={styles.mobileTitle}>大满书院</h1>
        </header>

        <div className={styles.content}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}